import { useRef } from 'react'

export const RETURN_TO_KEY = 'tracke5.return_to'
const DEFAULT = '/overview'

/**
 * An origin no host can be, used only to resolve a stored path against.
 *
 * `.invalid` is reserved by RFC 2606 precisely so it can never resolve. The
 * real origin is not used deliberately: this module has to behave identically
 * in a test, on localhost and on app-dev, and reading `window.location` here
 * would make the guard's answer depend on where it runs.
 */
const SAFE_ORIGIN = 'https://return-to.invalid'

/**
 * Where to go after signing in — in `sessionStorage`, not in router state.
 *
 * `signInWithRedirect` leaves the origin and comes back as a NEW document, so
 * `location.state` is gone by the time we can read it. That path is taken by
 * every coarse-pointer user (M1-R23), which is to say every phone — and
 * pre-login is the one genuinely mobile-first area of the app. The obvious
 * implementation loses the destination for the majority of the users it is for.
 */
export function rememberReturnTo(path: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path)
  } catch {
    // Storage refused — blocked cookies, a sandboxed iframe, an enterprise
    // policy. The user lands on Overview instead of their deep link, which is a
    // downgrade and not a failure. A throw here would be above every error
    // boundary and would cost the whole page.
  }
}

/** Drop any stored destination — a revoked session must land on Overview, not a deep link. */
export function forgetReturnTo(): void {
  try {
    sessionStorage.removeItem(RETURN_TO_KEY)
  } catch {
    // Storage refused; there is nothing stored to leak.
  }
}

/**
 * Is this something we are willing to navigate to?
 *
 * Resolved rather than inspected. The prefix test everyone writes —
 * `startsWith('/') && !startsWith('//')` — is beaten by `/\evil.test`: WHATWG
 * URL parsing folds backslashes into forward slashes, so the browser reads that
 * as the host `evil.test` while the string itself begins with exactly one
 * slash. Resolving against a fixed origin and comparing the result asks the
 * question that actually matters: does this stay on our origin?
 *
 * **And then it asks it again about the answer**, which is not redundant. A
 * dot segment eats the slash before it, so `/..//evil.test` resolves with
 * `origin === SAFE_ORIGIN` and passes the first check — while normalising to
 * the pathname `//evil.test`, which is protocol-relative. Returning that hands
 * the router a value this function has just certified: `history.replaceState`
 * throws SecurityError on it, and react-router's push path catches the failure
 * and falls back to `location.assign(url)`, which navigates off-origin.
 * web-tester found it. Normalisation happens BETWEEN the input and the output,
 * so checking only the input checks the wrong string.
 */
function samePath(stored: string): string | null {
  if (!stored.startsWith('/')) return null
  let url: URL
  try {
    url = new URL(stored, SAFE_ORIGIN)
  } catch {
    return null
  }
  if (url.origin !== SAFE_ORIGIN) return null
  // The NORMALISED form, not the stored text: `/a/../b` and `/b` are the same
  // destination, and handing the router the raw string would let a stored value
  // differ from the URL it produces.
  const path = `${url.pathname}${url.search}${url.hash}`
  // Re-resolved, because the value that leaves here is the one that gets
  // navigated to — see the header. This is the check, not a belt for a brace.
  try {
    if (new URL(path, SAFE_ORIGIN).origin !== SAFE_ORIGIN) return null
  } catch {
    return null
  }
  return path
}

/**
 * Read once and clear. A return-to that outlives its sign-in hijacks the next one.
 *
 * **This CONSUMES, so it must never be called from a render body.** Use
 * `useReturnTo` from a component. The distinction is not pedantry: it cost the
 * feature, and silently — see that hook's header.
 */
export function takeReturnTo(): string {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
  } catch {
    return DEFAULT
  }
  if (stored === null) return DEFAULT
  return samePath(stored) ?? DEFAULT
}

/**
 * The destination, consumed once — and only when the caller is ready to use it.
 *
 * **Why it is a hook at all.** `takeReturnTo()` removes the key it reads, so
 * calling it while rendering makes the render impure, and `main.tsx` wraps the
 * app in `<StrictMode>`, which double-invokes render in development precisely
 * to surface that. The first pass consumed `/budget` and was discarded; the
 * second read an empty key and returned `/overview`; the second committed. So
 * M1-R7 failed in the dev build on exactly the coarse-pointer redirect path the
 * guard exists for, while a test using a bare `render()` stayed green.
 * web-architect found it; `RequireAuth.test.tsx` now mounts under `<StrictMode>`
 * to keep it found. Production was not safe either, only luckier — any
 * re-render before the navigation commits consumed the key the same way.
 *
 * **A lazy ref, not `useState(() => takeReturnTo())`.** React double-invokes
 * state initialisers under StrictMode too, so the obvious spelling has the
 * identical bug.
 *
 * **Why `ready`, and it is the subtle half.** Hooks cannot be called
 * conditionally, so the first fix consumed on every mount of the guard —
 * including while the user was still signed OUT and merely looking at the
 * sign-in form. That destroys the one case this module exists for: arriving at
 * /signin would empty storage into a ref, and `signInWithRedirect` then
 * replaces the document, taking the ref with it. The user comes back to a fresh
 * page with nothing stored and lands on Overview — M1-R7 broken by the fix for
 * M1-R7. Consuming only once the session has actually arrived is what makes the
 * value survive the round trip.
 */
export function useReturnTo(ready: boolean): string {
  const destination = useRef<string | null>(null)
  if (ready && destination.current === null) destination.current = takeReturnTo()
  return destination.current ?? DEFAULT
}
